/**
 * Media Buyer Command Center (Task 207) — pure presentation logic, extracted
 * from `MediaBuyerCommandCenter.tsx` so it is unit-testable without importing
 * React. No fetching, no permission gating — display mapping only. The FE gate
 * (media_buyer + multi-client) lives in `AppLanding`; RLS stays the
 * load-bearing security gate.
 *
 * Scope note: the Media Buyer surface is driven by `me.client_assignments`
 * (ASSIGNED clients only), NOT the org-wide client list.
 *
 * The small status→label / status→risk / Arabic-numeral display helpers are
 * the SAME primitives the other command centers use, so this module re-exports
 * them from `ownerCommandCenter.logic` (clean DRY reuse of pure functions — no
 * refactor of the owner surface). The paid-media follow-up builder and the
 * PLATFORMS table below are MB-specific.
 */

import type { ClientAssignmentSummary } from '../api/types';

export {
  SOON,
  clientStatusLabelAr,
  statusBadgeClass,
  statusRisk,
  statusWeight,
  avatarColor,
  initial,
  toArabicDigits,
  AVATAR_COLORS,
} from './ownerCommandCenter.logic';

/** Icons used by derived follow-ups (a subset of the MB icon set). */
export type PaidMediaFollowUpIcon = 'flame' | 'plug';

export interface PaidMediaFollowUp {
  readonly key: string;
  readonly tone: string;
  readonly sev: 'urgent' | 'high' | 'mid';
  readonly sevLabel: string;
  readonly icon: PaidMediaFollowUpIcon;
  readonly title: string;
  readonly why: string;
  readonly to: string;
  readonly action: string;
}

/**
 * Build the "campaigns needing action today" queue from REAL assigned-client
 * status only — paused accounts (urgent: review paid media before spend
 * leaks) and onboarding accounts (mid: stand up ad accounts). No fabricated
 * campaign metrics (no invented ROAS/CPA/spend); every item links to the real
 * per-client paid-media workspace. Capped at 6. Operates on the media buyer's
 * OWN assignments (assigned-only scope).
 */
export function buildPaidMediaFollowUps(
  assignments: ReadonlyArray<ClientAssignmentSummary>,
): ReadonlyArray<PaidMediaFollowUp> {
  const out: PaidMediaFollowUp[] = [];
  for (const a of assignments) {
    if (a.client_status === 'paused') {
      out.push({
        key: `paused-${a.client_id}`,
        tone: 'occ-dec-rose',
        sev: 'urgent',
        sevLabel: 'عاجل',
        icon: 'flame',
        title: `عميل متوقّف — راجع حملاته المدفوعة — ${a.client_name}`,
        why: 'الحساب متوقّف؛ راجع حملاته المدفوعة وأوقف أي صرف بلا عائد قبل أن يتسرّب، أو صعّد لمدير التسويق.',
        to: `/app/clients/${a.client_id}/paid-media`,
        action: 'فتح الإعلانات',
      });
    }
  }
  for (const a of assignments) {
    if (a.client_status === 'onboarding') {
      out.push({
        key: `onb-${a.client_id}`,
        tone: 'occ-dec-plum',
        sev: 'mid',
        sevLabel: 'متوسط',
        icon: 'plug',
        title: `عميل جديد — جهّز حساباته الإعلانية — ${a.client_name}`,
        why: 'أكمل ربط الحسابات الإعلانية وجهّز أول حملات العميل الجديد على Meta و Snapchat.',
        to: `/app/clients/${a.client_id}/paid-media`,
        action: 'فتح الإعلانات',
      });
    }
  }
  return out.slice(0, 6);
}

/**
 * Platform/account-health model. Product rules (Task 207):
 *  - Meta + Snapchat are the supported platforms, but with NO live landing
 *    feed they render as `pending` («قيد الربط»), never a fabricated "active /
 *    synced" state.
 *  - TikTok + Google MUST stay `soon` («قريباً») until real linked data
 *    exists — TikTok must not appear fully active by default.
 *  - Every account action is DISABLED: media_buyer holds only paid-media READ
 *    permissions, so reconnect is a (disabled) "طلب إعادة ربط" request, not a
 *    direct OAuth/sync action, and there are no write controls.
 */
export type PlatformState = 'pending' | 'soon';

export interface PlatformHealth {
  readonly key: string;
  readonly name: string;
  readonly abbr: string;
  readonly accent: string;
  readonly state: PlatformState;
  readonly stateLabel: string;
  readonly sync: string;
  readonly action: string;
  readonly actionDisabled: boolean;
  /** True for platforms selectable in the filter (Meta/Snapchat); soon ones are not. */
  readonly filterable: boolean;
}

export const PLATFORMS: ReadonlyArray<PlatformHealth> = [
  {
    key: 'meta',
    name: 'Meta Ads',
    abbr: 'M',
    accent: 'var(--sky)',
    state: 'pending',
    stateLabel: 'قيد الربط',
    sync: 'بانتظار ربط بيانات الأداء المباشرة',
    action: 'طلب إعادة ربط',
    actionDisabled: true,
    filterable: true,
  },
  {
    key: 'snapchat',
    name: 'Snapchat Ads',
    abbr: 'S',
    accent: 'var(--butter)',
    state: 'pending',
    stateLabel: 'قيد الربط',
    sync: 'بانتظار ربط بيانات الأداء المباشرة',
    action: 'طلب إعادة ربط',
    actionDisabled: true,
    filterable: true,
  },
  {
    key: 'tiktok',
    name: 'TikTok Ads',
    abbr: 'T',
    accent: 'var(--ink)',
    state: 'soon',
    stateLabel: 'قريباً',
    sync: 'التكامل قيد الإعداد — لا توجد بيانات بعد',
    action: 'غير مفعّل بعد',
    actionDisabled: true,
    filterable: false,
  },
  {
    key: 'google',
    name: 'Google Ads',
    abbr: 'G',
    accent: '#C0552C',
    state: 'soon',
    stateLabel: 'قريباً',
    sync: 'غير مربوط بعد',
    action: 'غير مفعّل بعد',
    actionDisabled: true,
    filterable: false,
  },
];
